// ─────────────────────────────────────────────────────────────────────────────
// Help drawer for the Project Tracker.
//
// Replaces the old first-run product tour (react-joyride). Instead of an
// anchored walkthrough, a persistent "?" button in the app header opens a
// right-side drawer that explains every part of the Project Tracker the
// signed-in user can actually reach.
//
// Access-aware: the entries are filtered against the user's roles/permissions
// (the same gates `App.tsx` and each module use), so the drawer never describes
// an area the user can't open. Each entry can also carry a short note about
// whether the user can edit or only view.
//
// The header button lives in App's <Header/> (outside the authenticated shell
// that holds `me`), while the drawer itself is mounted from <AuthenticatedShell/>
// where `me` is available. The two communicate through a tiny module-level
// controller (`useHelpController`/`openHelp`) — the same decoupling the tour
// used — rather than threading context through the app.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { isFieldLeaderOnly } from '../lib/access';

// ─── Access shape ────────────────────────────────────────────────────────────
// Mirrors the fields `usersApi.me()` returns that gate visibility/editing.
export type HelpMe = {
  roles?: string[];
  permissions?: string[];
  /** Needed by `isFieldLeaderOnly` — see `canViewToday`. */
  canViewAll?: boolean;
  /** One half of the Today gate — see `canViewToday`. */
  hasActiveAssignments?: boolean;
} | null | undefined;

export type Access = {
  isAdmin: boolean;
  /**
   * The Feedback Admin role (`feedback.manage`): the Admin tab, Feedback alone.
   * Not field-leader-only, for the same reason as Contracts below — `visibleTabs`
   * gives that account Projects and nothing else, so the Admin tab is not theirs.
   */
  canManageFeedback: boolean;
  canViewReports: boolean;
  canEditProjects: boolean;
  canEditEquipment: boolean;
  canEditReceiving: boolean;
  canEditMobilizations: boolean;
  /** Financials + Commitments are gated on a permission AND the admin cost toggle. */
  canViewFinancials: boolean;
  /**
   * Contracts. contracts.view or contracts.edit (which also reads), and not
   * field-leader-only: their navigation is Projects alone and the job's Contracts
   * tab is an office tab, so documenting it would describe a screen they cannot open.
   */
  canViewContracts: boolean;
  canEditContracts: boolean;
  /**
   * Today. TWO conditions, and both are easy to state by halves:
   * a live project assignment AND not being field-leader-only.
   *
   * ⚠️ The second half is not redundant. Leading a crew on a job IS an
   * assignment — `activeAssignmentWhere` counts a `project_field_leaders` row —
   * so a field-leader-only user with a live job has `hasActiveAssignments`
   * true while `visibleTabs` gives them Projects and nothing else and
   * `todayRouteState` redirects them. Gating on the assignment alone documents
   * a tab that is not in their navigation. (Codex, #512.)
   *
   * Documenting a screen somebody cannot open is the same failure as hiding
   * one they can, and this guide's whole premise is that a reader who finds
   * one wrong claim stops trusting the rest.
   */
  canViewToday: boolean;
  /**
   * The company-wide Daily Logs tab. Not field-leader-only, for the same reason
   * as Contracts and Today: their navigation is Projects alone and the route
   * redirects them, so documenting it would describe a screen they cannot open.
   * (A field leader still has the Daily Logs tab INSIDE each job.)
   */
  canViewDailyLogsRollup: boolean;
};

/** Exported so the gating can be tested directly — see HelpDrawer.spec.ts. */
export function toAccess(me: HelpMe): Access {
  const roles = me?.roles ?? [];
  const perms = me?.permissions ?? [];
  const isAdmin = roles.includes('Admin');
  const can = (p: string) => isAdmin || perms.includes(p);
  // Worked out ONCE: the same viewer goes into every field-leader check below, so
  // the three cannot drift apart (see the header of lib/access.ts on why).
  const fieldLeaderOnly = isFieldLeaderOnly({
    roles,
    permissions: perms,
    canViewAll: me?.canViewAll ?? false,
  });
  return {
    isAdmin,
    // ⚠️ SAME NAME AS `NavAccess.canManageFeedback`, DIFFERENT DEFINITION. That one
    // is the raw `feedback.manage` grant, and `visibleTabs` applies the Admin and
    // field-leader rules around it. This one has them folded in, like
    // `canViewContracts`, and is therefore TRUE FOR ADMIN too — which is why the
    // Feedback-only entry also tests `!a.isAdmin`.
    canManageFeedback: can('feedback.manage') && !fieldLeaderOnly,
    canViewReports: can('reports.view'),
    canEditProjects: can('projects.edit'),
    canEditEquipment: can('equipment.edit'),
    canEditReceiving: can('receiving.edit'),
    canEditMobilizations: can('mobilizations.edit'),
    canViewFinancials: can('financials.view'),
    canViewContracts: (can('contracts.view') || can('contracts.edit')) && !fieldLeaderOnly,
    canEditContracts: can('contracts.edit'),
    // ⚠️ NOT `can(...)` — there is no permission behind this, and Admin does
    // not bypass it. Mirrors `canViewToday` + the field-leader guard in
    // App.tsx / navAccess.ts, via the SHARED predicate rather than a fourth
    // hand-rolled copy (see the header of lib/access.ts on why).
    canViewToday: (me?.hasActiveAssignments ?? false) && !fieldLeaderOnly,
    canViewDailyLogsRollup: !fieldLeaderOnly,
  };
}

// ─── Help content ────────────────────────────────────────────────────────────
// `show` (optional) hides an entry the user can't reach. `note` (optional)
// returns a short access caption (e.g. "You can view and edit" vs "View only").
type Entry = {
  title: string;
  body: string;
  show?: (a: Access) => boolean;
  note?: (a: Access) => string | null;
};
type Section = { heading: string; blurb: string; entries: Entry[] };

const editNote = (label: string) => (canEdit: boolean) =>
  canEdit ? `You can view and ${label}.` : 'View only — ask an admin for edit access.';

export const SECTIONS: Section[] = [
  {
    heading: 'Across the Project Tracker',
    blurb: 'The tabs along the top move between company-wide areas. You see the ones your access allows.',
    entries: [
      {
        title: 'Today',
        body:
          'Your own work, gathered into one screen — the jobs you’re named on, what’s starting in the next three weeks and how ready it is, what’s gone quiet, and recent activity. It’s a reading screen: nothing is edited here, and any row or chip naming a single job opens it — though the counts along the top are figures rather than links, and a row covering several jobs has none to open. Two things worth knowing. The three lanes are worked out from dates, not set by hand, so a row moves itself as its date approaches; a job with more than half its critical readiness criteria still unset is also held at no lower than This week, though it still moves up to Due today when its own date warrants — that rule sets a floor, not a destination. The readiness check reaches only a limited distance ahead, and how far is an admin setting, so a job beyond that window can show a poor figure without raising a row yet. And nothing here is an empty result dressed up as a finished one: the safety card says outright that it isn’t built yet, and the two recurring deadline tiles aren’t drawn at all because nothing feeds them — a number on this page always means the query ran and counted.',
        show: (a) => a.canViewToday,
      },
      {
        title: 'Projects',
        body: 'The home list of every job you’re assigned to. Search, sort, and open any project to its workspace. Click a project to drill into its full command center. The Planned Start column is the date a job is currently working to — the same date on its Schedule & Dates card — and it is what the sort and the start-date filter use. When work actually began is a field-hours fact rather than a plan, so it lives on the project’s Manpower tab beside the hours it comes from.',
      },
      {
        title: 'Schedule',
        body: 'A company-wide rollup of critical milestones across every job, so you can scan what’s coming due without opening each project.',
      },
      {
        title: 'Manpower',
        body: 'Field crew counts and hours across all jobs — a forecast view and a by-field-leader view, sourced from daily labor data.',
      },
      {
        title: 'Equipment',
        body: 'Equipment requests across every job. Track what’s been requested and its status.',
        note: (a) => editNote('add or update requests')(a.canEditEquipment),
      },
      {
        title: 'Survey',
        body: 'Survey requests and visit logs. New requests open the intake form and flow back into this list automatically.',
      },
      {
        title: 'Receiving',
        body: 'Shipping & receiving order intake — log incoming orders and track them, optionally linked to a project.',
        note: (a) => editNote('create or update orders')(a.canEditReceiving),
      },
      {
        title: 'Contracts',
        body: 'The weekly subcontract status report, live. Every subcontract by where it stands, and which of its forms have come in — the signed contract, W9, certificate of insurance and, for a new vendor, the new vendor form. A sub is cleared to report to the jobsite once all of them are in. Owner contracts under review are listed below. A project manager asks for a new subcontract with the Request subcontract button on a job’s Contracts tab: fill in the form and submit it, and it appears here for the coordinator, who creates the subcontract in Intacct, types its number onto the request and downloads the Word contract, along with any extra documents the requester attached to it.',
        show: (a) => a.canViewContracts,
        note: (a) => editNote('update subcontracts and owner contracts')(a.canEditContracts),
      },
      {
        title: 'QA / QC',
        body: 'Company-wide pour cards (concrete and rock/aggregate orders) and inspections — what needs an office action across every job.',
      },
      {
        title: 'Safety',
        body: 'A company-wide safety rollup summarizing safety activity by job.',
      },
      {
        title: 'Daily Logs',
        body: 'Every job’s daily logs in one list, newest first — date, job, author, weather, crew size, man-hours, summary and status — so you don’t have to open each job to find one. It opens on the last 30 days; change the dates, filter by status, or search by job number or name, author, or the work done. Click a row to open that log in its job. You see the logs for the jobs your access covers.',
        show: (a) => a.canViewDailyLogsRollup,
      },
      {
        title: 'Reports',
        body: 'Cross-project reporting such as the Project Readiness Report, with CSV export.',
        show: (a) => a.canViewReports,
      },
      {
        title: 'PPR — Post Project Review',
        body: 'Post Project Review records captured at project close-out.',
      },
      {
        title: '⚙ Admin',
        body: 'Manage users, roles, and permissions — including who can see Reports and edit each area.',
        show: (a) => a.isAdmin,
      },
      {
        // Same tab, a different page: the Feedback Admin role opens Admin and is
        // shown Feedback alone. Mutually exclusive with the entry above, so
        // nobody is told about both.
        title: '⚙ Admin — Feedback',
        body: 'Review what people send from the 💬 Feedback button — read it, download any files they attached, and close an item once it is dealt with (or reopen it). This is the only part of Admin your access opens.',
        show: (a) => a.canManageFeedback && !a.isAdmin,
      },
    ],
  },
  {
    heading: 'Inside a project',
    blurb: 'Open any project from the Projects list to reach these tabs. Each one is a piece of that job.',
    entries: [
      {
        title: 'Overview',
        body: 'The project’s key details, contract value, health, and team. Sections edit in place — there’s no separate edit pop-up.',
        note: (a) => editNote('edit project details')(a.canEditProjects),
      },
      {
        title: 'Weekly Report',
        body: 'One week’s project report, ready for Word: project details, the team, last week’s daily logs with photos, manpower and equipment. Export an internal copy, or a client copy with only the days you choose. Once sent, upload the finished file to its week: weeks go in order, a ! marks a week still owed, the uploaded report shows under After upload, and what you typed starts next week’s report.',
        note: (a) => editNote('export and upload the Word reports')(a.canEditProjects),
      },
      {
        title: 'Daily Logs',
        body: 'The daily field record — notes, crew, photos, and same-day weather. Field crews can add photos straight from the job.',
      },
      {
        title: 'Mobilization',
        body: 'The mobilization checklist for standing the job up.',
        note: (a) => editNote('update the checklist')(a.canEditMobilizations || a.canEditProjects),
      },
      {
        title: 'Schedule',
        body: 'This project’s schedule and milestones.',
      },
      {
        title: 'Manpower',
        body: 'Three views of labor on this job: the crew scheduled ahead, scheduled against what was actually worked week by week, and the actual daily crew and hours from payroll.',
      },
      {
        title: 'Equipment',
        body: 'Equipment requested for this specific job.',
        note: (a) => editNote('add or update requests')(a.canEditEquipment),
      },
      {
        title: 'Survey',
        body: 'Survey requests tied to this job.',
      },
      {
        title: 'Receiving',
        body: 'Shipping & receiving orders linked to this job.',
        note: (a) => editNote('create or update orders')(a.canEditReceiving),
      },
      {
        title: 'RFIs',
        body: 'The full RFI lifecycle — draft emails to Outlook, upload files to the SharePoint Job Folder, and follow a complete activity history. RFIs are auto-numbered.',
      },
      {
        title: 'Submittals',
        body: 'The full Submittal lifecycle, mirroring RFIs — drafts, file uploads, and activity history with auto-numbering.',
      },
      {
        title: 'Safety',
        body: 'Safety activity for this job (read-only mirror of the safety system).',
      },
      {
        title: 'Materials',
        body: 'Concrete and material commitments for the job, tracked against the purchase orders synced from Sage Intacct.',
      },
      {
        title: 'Documents',
        body: 'Job documents held in the SharePoint job folder, listed here so you don’t have to go hunting for the folder.',
      },
      {
        title: 'Drawings',
        body: 'Drawing sets and revisions — which set is current, and what changed.',
      },
      {
        title: 'Change Mgmt',
        body: 'Change orders against the contract, including pending ones that aren’t in the contract value yet.',
      },
      {
        title: 'QA / QC',
        body: 'Pour cards and quality inspections mirrored from the Formstack forms the crew fills in. Office actions here are confirming an order and marking a pour complete. This tab also holds the job\'s approved concrete Mix IDs — the PM keeps that list, and incoming pours are checked against it.',
      },
      {
        title: 'Financials',
        body: 'Contract, budget, actual cost and margin for the job, plus the cost-to-complete forecast a PM enters each month. Health of the numbers, not of the schedule.',
        show: (a) => a.canViewFinancials,
        note: () => 'Needs Financials access and the cost display turned on.',
      },
      {
        title: 'Commitments',
        body: 'What’s been bought against each cost code — purchase orders and subcontracts — and what’s still to buy out, plus the month-end accrual schedule. Replaces the old Cost tab.',
        show: (a) => a.canViewFinancials,
        note: () => 'Needs Financials access and the cost display turned on.',
      },
      {
        title: 'Contracts',
        body: 'This job’s subcontracts and where each stands on its paperwork, plus any owner contract linked to the job. Request subcontract starts the form that collects everything the Word contract needs (use Save draft at any point and come back, then submit it to the coordinator). Choose Time and material, not to exceed as the contract basis when the final amount will depend on actual hours and materials; the contract then says so, and can state a not-to-exceed rate. Once it is saved, its page can also hold optional extra documents, such as an exhibit or a bond form, for the coordinator to download. Your requests on this job are listed above the subcontracts.',
        show: (a) => a.canViewContracts,
        note: (a) => editNote('update subcontracts and owner contracts')(a.canEditContracts),
      },
      {
        title: 'Reports',
        body: 'Reports scoped to this project.',
      },
    ],
  },
];

// ─── Module-level open/close controller ──────────────────────────────────────
// The header button and the drawer are mounted in different parts of the tree;
// a tiny shared store lets the button toggle the drawer without React context.
let open = false;
const listeners = new Set<() => void>();
function emit() {
  for (const l of listeners) l();
}
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function getOpen(): boolean {
  return open;
}
function setOpen(next: boolean) {
  if (open === next) return;
  open = next;
  emit();
}

/** Open the help drawer from anywhere — e.g. the header "?" button. */
export function openHelp() {
  setOpen(true);
}
/** Hook for the trigger side (header button). Returns `{ openHelp }`. */
export function useHelpController() {
  return { openHelp };
}

// ─── Styles (E&W --ew- tokens) ───────────────────────────────────────────────
const STYLE = `
  .help-overlay {
    position: fixed; inset: 0; z-index: 10000;
    background: rgba(54, 54, 54, 0.45);
    opacity: 0; pointer-events: none;
    transition: opacity var(--ew-dur-fast) var(--ew-ease);
  }
  .help-overlay.open { opacity: 1; pointer-events: auto; }

  .help-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; z-index: 10001;
    width: min(440px, 92vw);
    background: var(--ew-white);
    box-shadow: var(--ew-shadow-3);
    display: flex; flex-direction: column;
    transform: translateX(100%);
    transition: transform var(--ew-dur-base, 0.24s) var(--ew-ease);
    font-family: var(--ew-font);
  }
  .help-drawer.open { transform: translateX(0); }

  .help-hd {
    display: flex; align-items: center; gap: var(--ew-s-3);
    padding: var(--ew-s-4) var(--ew-s-5);
    border-bottom: 1px solid var(--ew-n-200);
    flex-shrink: 0;
  }
  .help-hd h2 {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font);
    color: var(--ew-dark); margin: 0; flex: 1;
  }
  .help-close {
    width: 30px; height: 30px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--ew-r-pill);
    border: 1px solid var(--ew-n-300); background: var(--ew-white);
    color: var(--ew-n-500); font-size: 16px; line-height: 1; cursor: pointer;
    transition: color var(--ew-dur-fast) var(--ew-ease), border-color var(--ew-dur-fast) var(--ew-ease);
  }
  .help-close:hover { color: var(--ew-dark); border-color: var(--ew-dark); }

  .help-body { overflow-y: auto; padding: var(--ew-s-5); }
  .help-intro {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font);
    color: var(--ew-n-600); margin-bottom: var(--ew-s-5);
  }
  .help-section { margin-bottom: var(--ew-s-6); }
  .help-section-hd {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase;
    color: var(--ew-green); margin: 0 0 var(--ew-s-2);
  }
  .help-section-blurb {
    font: var(--ew-fw-regular) var(--ew-fs-meta)/var(--ew-lh-body) var(--ew-font);
    color: var(--ew-n-500); margin: 0 0 var(--ew-s-4);
  }
  .help-entry { padding: var(--ew-s-3) 0; border-top: 1px solid var(--ew-n-100); }
  .help-entry:first-of-type { border-top: 0; }
  .help-entry-title {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.2 var(--ew-font);
    color: var(--ew-dark); margin: 0 0 4px;
  }
  .help-entry-body {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font);
    color: var(--ew-n-700); margin: 0;
  }
  .help-entry-note {
    display: inline-block; margin-top: 6px;
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1.3 var(--ew-font);
    color: var(--ew-n-500);
  }
`;

// ─── The mountable drawer ────────────────────────────────────────────────────
// Mount once from AuthenticatedShell with the resolved `me`. Renders nothing
// visible until opened via openHelp().
export default function HelpDrawer({ me }: { me: HelpMe }) {
  const isOpen = useSyncExternalStore(subscribe, getOpen, getOpen);
  const access = toAccess(me);

  // Close on Escape while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <>
      <style>{STYLE}</style>
      <div
        className={`help-overlay${isOpen ? ' open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden={!isOpen}
      />
      <aside
        className={`help-drawer${isOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Project Tracker help"
        aria-hidden={!isOpen}
      >
        <div className="help-hd">
          <h2>Project Tracker help</h2>
          <button className="help-close" onClick={() => setOpen(false)} aria-label="Close help">
            ✕
          </button>
        </div>
        <div className="help-body">
          <p className="help-intro">
            A quick tour of what you can see and do in the Project Tracker.
          </p>
          {/* The drawer answers "what is this tab"; the training guide teaches the
              job, role by role. Keeping the "?" as the single entry point and
              linking on from here means people only have one thing to remember. */}
          <Link
            to="/training"
            onClick={() => setOpen(false)}
            style={{
              display: 'block', margin: '0 0 18px', padding: '11px 13px',
              background: '#E7F0F8', border: '1px solid #CFE0EF', borderRadius: 'var(--ew-r-md)',
              color: '#15659E', textDecoration: 'none',
              font: 'var(--ew-fw-regular) 13px/1.5 var(--ew-font)',
            }}
          >
            <b>Full training guide →</b>
            <br />
            Step-by-step for your role — what you own, in the order you meet it.
          </Link>
          {SECTIONS.map((section) => {
            const visible = section.entries.filter((e) => !e.show || e.show(access));
            if (visible.length === 0) return null;
            return (
              <section className="help-section" key={section.heading}>
                <h3 className="help-section-hd">{section.heading}</h3>
                <p className="help-section-blurb">{section.blurb}</p>
                {visible.map((e) => {
                  const note = e.note?.(access) ?? null;
                  return (
                    <div className="help-entry" key={e.title}>
                      <p className="help-entry-title">{e.title}</p>
                      <p className="help-entry-body">{e.body}</p>
                      {note ? <span className="help-entry-note">{note}</span> : null}
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      </aside>
    </>
  );
}
